/**
 * Variant selector — the single place the shell goes to look up "what does
 * the per-game hub and game renderer look like, given the active variant?"
 *
 * The top-level Games Hub at `/` is variant-independent — it's the shared
 * editorial printed-page landing. Each game's *own* hub (e.g. the trivia
 * categories page at `/trivia`, or the sudoku Easy/Medium/Hard picker at
 * `/sudoku`, or the word-search themed picker at `/word-search`) and its
 * in-game renderer come from the active variant.
 *
 * Variants ship a subset of game renderers — every field on `Variant` besides
 * `manifest` is optional. Reading Room ships Trivia today; Sudoku Pencilmarks
 * ships Sudoku; Wordsearch Themedshape ships Word Search. If a variant doesn't
 * ship a given game's surface, the shell falls through to any other registered
 * variant that does. That fall-through is what lets the reading-room user
 * reach a playable sudoku or word-search today, before reading-room grows its
 * own skins for those.
 *
 * The active variant is selected by `getActiveVariantSlug()` below. Today it
 * returns a constant; when more variants land this becomes a Preferences-
 * backed selector with a host-config override.
 */

import type { ComponentType } from 'react';

import * as readingRoom from './reading-room';
import * as sudokuPencilmarks from './sudoku-pencilmarks';
import * as categoriesClassic from './categories';
import * as wordsearchThemedshape from './wordsearch-themedshape';
import type { VariantManifest } from './reading-room';

export type VariantSlug =
  | 'reading-room'
  | 'sudoku-pencilmarks'
  | 'categories'
  | 'wordsearch-themedshape';

interface Variant {
  manifest: VariantManifest;
  /** The trivia categories hub (route: `/trivia`). */
  Hub?: ComponentType;
  /** The sudoku difficulty picker hub (route: `/sudoku`). */
  SudokuHub?: ComponentType;
  /** The word-search themed-category picker hub (route: `/word-search`). */
  WordSearchHub?: ComponentType;
  /** The Categories hub (route: `/categories`). */
  CategoriesHub?: ComponentType;
  /** In-game trivia renderer mounted by `games/trivia/index.tsx`. */
  Trivia?: typeof readingRoom.Trivia;
  /** In-game sudoku renderer mounted by `games/sudoku/index.tsx`. */
  Sudoku?: typeof sudokuPencilmarks.Sudoku;
  /** In-game word-search renderer mounted by `games/word-search/index.ts`. */
  WordSearch?: typeof wordsearchThemedshape.WordSearch;
  /** In-game Categories renderer mounted by `games/categories/index.tsx`. */
  Categories?: typeof categoriesClassic.Categories;
}

const VARIANTS: Record<VariantSlug, Variant> = {
  'reading-room': {
    manifest: readingRoom.manifest,
    Hub: readingRoom.Hub,
    Trivia: readingRoom.Trivia,
  },
  'sudoku-pencilmarks': {
    manifest: sudokuPencilmarks.manifest,
    SudokuHub: sudokuPencilmarks.SudokuHub,
    Sudoku: sudokuPencilmarks.Sudoku,
  },
  categories: {
    manifest: categoriesClassic.manifest,
    CategoriesHub: categoriesClassic.CategoriesHub,
    Categories: categoriesClassic.Categories,
  },
  'wordsearch-themedshape': {
    manifest: wordsearchThemedshape.manifest,
    WordSearchHub: wordsearchThemedshape.WordSearchHub,
    WordSearch: wordsearchThemedshape.WordSearch,
  },
};

/**
 * Resolve the variant slug to use. Currently a constant; when more variants
 * land we'll wire this to a Preferences setting and a host-config override.
 */
export function getActiveVariantSlug(): VariantSlug {
  return 'reading-room';
}

/**
 * Look up the active variant, *merged* with fall-through surfaces from other
 * registered variants for games the active variant doesn't ship. Reading Room
 * (active) doesn't yet have a sudoku or word-search skin, so those slots fall
 * through to sudoku-pencilmarks and wordsearch-themedshape respectively. The
 * active variant's own surfaces always win when present.
 */
export function getActiveVariant(): Variant {
  const active = VARIANTS[getActiveVariantSlug()];
  const merged: Variant = { manifest: active.manifest };
  // Walk every registered variant once, taking the first definition we see
  // for each slot, starting with the active one.
  const order: ReadonlyArray<Variant> = [active, ...Object.values(VARIANTS).filter((v) => v !== active)];
  for (const v of order) {
    if (!merged.Hub && v.Hub) merged.Hub = v.Hub;
    if (!merged.SudokuHub && v.SudokuHub) merged.SudokuHub = v.SudokuHub;
    if (!merged.WordSearchHub && v.WordSearchHub) merged.WordSearchHub = v.WordSearchHub;
    if (!merged.CategoriesHub && v.CategoriesHub) merged.CategoriesHub = v.CategoriesHub;
    if (!merged.Trivia && v.Trivia) merged.Trivia = v.Trivia;
    if (!merged.Sudoku && v.Sudoku) merged.Sudoku = v.Sudoku;
    if (!merged.WordSearch && v.WordSearch) merged.WordSearch = v.WordSearch;
    if (!merged.Categories && v.Categories) merged.Categories = v.Categories;
  }
  return merged;
}

/** Look up any variant by slug. */
export function getVariant(slug: VariantSlug): Variant {
  return VARIANTS[slug];
}

export type { Variant };
